import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip'; // For attendee status
import { useListEventComments, useListEventAttendees } from '../services/appService'; // Import the new hooks

// Helper to format date/time (can be moved to a utils file)
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleString(); // Simple format for now
    } catch (e) {
        console.error("[formatDate event details] Error formatting date:", dateInput, e);
        return 'Invalid Date';
    }
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-details-tabpanel-${index}`}
      aria-labelledby={`event-details-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}> {/* Add padding top for content */}
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `event-details-tab-${index}`,
    'aria-controls': `event-details-tabpanel-${index}`,
  };
}

function EventDetailsModal({ open, onClose, appId, eventId, eventTitle }) {
  const [comments, setComments] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0); // 0 for Comments, 1 for Attendees

  const listCommentsHook = useListEventComments();
  const listAttendeesHook = useListEventAttendees();

  const fetchDetails = useCallback(async () => {
    if (!appId || !eventId) {
      setComments([]);
      setAttendees([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Fetch both in parallel
      const [commentsData, attendeesData] = await Promise.all([
        listCommentsHook(appId, eventId),
        listAttendeesHook(appId, eventId)
      ]);
      console.log("[EventDetailsModal] Fetched comments:", commentsData);
      console.log("[EventDetailsModal] Fetched attendees:", attendeesData);
      setComments(commentsData || []);
      setAttendees(attendeesData || []);
    } catch (err) {
      console.error("Error fetching event details:", err);
      setError(err.message || 'Failed to load event details.');
      setComments([]);
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  }, [appId, eventId, listCommentsHook, listAttendeesHook]);

  useEffect(() => {
    if (open && eventId) {
      fetchDetails();
    } else {
        // Clear state when modal is closed or eventId is missing
        setComments([]);
        setAttendees([]);
        setError('');
        setSelectedTab(0); // Reset tab
    }
  }, [open, eventId, fetchDetails]); // Rerun if eventId changes while open

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'attending': return 'success';
      case 'maybe': return 'warning';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Details for: {eventTitle || `Event (${eventId ? eventId.substring(0, 8) : ''}...)`}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
        {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Event details tabs">
            <Tab label={`Comments (${loading ? '...' : comments.length})`} {...a11yProps(0)} />
            <Tab label={`Attendees (${loading ? '...' : attendees.length})`} {...a11yProps(1)} />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}> {/* Allow content to scroll */}
            <TabPanel value={selectedTab} index={0}>
              {comments.length === 0 ? (
                  <Typography>No comments found.</Typography>
              ) : (
                <List dense>
                  {comments.map((comment) => (
                    <ListItem key={comment.id} alignItems="flex-start">
                      <ListItemText
                        primary={comment.text}
                        secondary={
                          <Typography component="span" variant="body2" color="textSecondary">
                            By: {comment.user_name || 'Unknown'} on {formatDate(comment.created_at)}
                          </Typography>
                        }
                      />
                      {/* Add delete button if needed later */}
                    </ListItem>
                  ))}
                </List>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              {attendees.length === 0 ? (
                  <Typography>No attendees found.</Typography>
              ) : (
                <List dense>
                  {attendees.map((attendee) => (
                    <ListItem key={attendee.id}>
                      <ListItemText
                        primary={attendee.user_name || 'Unknown'}
                        secondary={`Registered on: ${formatDate(attendee.created_at)}`}
                      />
                       <Chip
                          label={attendee.status || 'Unknown'}
                          color={getStatusChipColor(attendee.status)}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                    </ListItem>
                  ))}
                </List>
              )}
            </TabPanel>
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

EventDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appId: PropTypes.string,
  eventId: PropTypes.string, // Can be null initially
  eventTitle: PropTypes.string, // Pass event title for better context
};

export default EventDetailsModal; 